import {
  Button,
  ButtonColorEnum,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  Tooltip,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Listbox, { ListboxSelected } from '@/components/Listbox';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { buildNewPresenceDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  INLINE_DIALOG_OPTIONS,
  PostLogoFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TClipLogo } from '@/features/clips/types';
import {
  getCachedSupportLabel,
  getSupportAliasById,
  setSupportLabelCache,
} from '@/features/programs/helpers/supports';
import { useSupportOptions } from '@/features/programs/hooks/useSupportOptions';
import { TSegmentLogo } from '@/features/segments/types';

type SupportInlineDropdownProps<T extends TSegmentLogo | TClipLogo> = {
  item: T;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostLogoFor<T>>;
  onPreview?: (item: T) => void;
};

export function SupportInlineDropdown<T extends TSegmentLogo | TClipLogo>({
  item,
  onUpdateInline,
  onPreview,
}: SupportInlineDropdownProps<T>) {
  const { logoData, playerVideoCurrentTime } = useBroadcastScheduleProgram();
  const { openInline, toggleInline, dropdownCoords, setDropdownCoordsFromRect } =
    useBroadcastScheduleContext();
  const { id: itemId } = item;
  const isOpen = openInline.support === itemId;
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const { allSupports, supportOptions, createSupportOption } = useSupportOptions();
  const [searchText, setSearchText] = useState('');
  const [optimisticSupport, setOptimisticSupport] = useState<{
    itemId: string;
    id: string;
    label: string;
  } | null>(null);
  const { t } = useTranslation();
  const supportCode = 'support_code' in item.parameters ? item.parameters.support_code : undefined;

  useLayoutEffect(() => {
    if (isOpen && btnRef.current) {
      if (logoData?.id !== item.id) {
        onPreview?.(item);
      }
      setDropdownCoordsFromRect(btnRef.current.getBoundingClientRect());
    }
  }, [isOpen]);

  const optimisticSupportForItem = optimisticSupport?.itemId === itemId ? optimisticSupport : null;
  const currentSupportId = item.parameters?.support_id ?? optimisticSupportForItem?.id;

  const selectedSupportLabel = useMemo(() => {
    if (optimisticSupportForItem && optimisticSupportForItem.id === currentSupportId) {
      return optimisticSupportForItem.label;
    }

    return (
      getSupportAliasById(currentSupportId, allSupports) ||
      item.support ||
      supportCode ||
      getCachedSupportLabel(currentSupportId) ||
      ''
    );
  }, [allSupports, currentSupportId, item.support, optimisticSupportForItem, supportCode]);

  const supportAlias = selectedSupportLabel || t('Sin definir');

  const mergedSupportOptions = useMemo(() => {
    if (!optimisticSupportForItem) return supportOptions;

    const optimisticOption = {
      label: optimisticSupportForItem.label,
      value: optimisticSupportForItem.id,
    };

    return supportOptions.some(option => String(option.value) === optimisticSupportForItem.id)
      ? supportOptions
      : [optimisticOption, ...supportOptions];
  }, [optimisticSupportForItem, supportOptions]);

  useEffect(() => {
    if (!optimisticSupportForItem) return;

    const itemHasCaughtUp = item.parameters?.support_id === optimisticSupportForItem.id;
    const supportsHaveCaughtUp = !!getSupportAliasById(optimisticSupportForItem.id, allSupports);

    if (itemHasCaughtUp && supportsHaveCaughtUp) {
      setOptimisticSupport(null);
    }
  }, [allSupports, item.parameters?.support_id, optimisticSupportForItem]);

  useEffect(() => {
    if (optimisticSupport && optimisticSupport.itemId !== itemId) {
      setOptimisticSupport(null);
    }
  }, [itemId, optimisticSupport]);

  if (!itemId) return <></>;

  const applySupport = (supportId: string, label?: string) => {
    const newLogoData = buildNewPresenceDataToSave(item);
    const applySilently = false;

    if (label) {
      setOptimisticSupport({ itemId, id: supportId, label });
      setSupportLabelCache(supportId, label);
    }

    newLogoData.parameters.support_id = supportId;
    onUpdateInline?.(newLogoData, applySilently, playerVideoCurrentTime);
    toggleInline(INLINE_DIALOG_OPTIONS.Support, itemId);
  };

  const handleCreateSupport = async (name: string) => {
    const { value: createdSupportId, label } = await createSupportOption(name);
    if (createdSupportId) {
      setSupportLabelCache(createdSupportId, label);
      applySupport(createdSupportId, label);
    }
    setSearchText('');
  };

  return (
    <div className="relative">
      <Button
        ref={btnRef}
        color={ButtonColorEnum.SECONDARY_OLD}
        className={clsx(
          'h-auto max-w-full cursor-pointer',
          openInline.support === itemId ? 'bg-neutral-20' : 'bg-transparent'
        )}
        onClick={() => {
          toggleInline(INLINE_DIALOG_OPTIONS.Support, itemId);
        }}
        rightIcon={() => (openInline.support === itemId ? <ChevronUpIcon /> : <ChevronDownIcon />)}
      >
        <Tooltip
          help={supportAlias}
          grow
          showCursorHelp={false}
          containerClasses="min-w-0 overflow-hidden"
        >
          <span className="block w-full truncate text-left text-sm capitalize">{supportAlias}</span>
        </Tooltip>
      </Button>
      {isOpen &&
        dropdownCoords &&
        createPortal(
          <div
            className="absolute z-100 mt-2 min-w-[300px] border border-neutral-20 bg-white p-4 shadow-lg"
            style={{
              top: dropdownCoords.top,
              left: dropdownCoords.left,
            }}
          >
            <Listbox
              key={`${itemId}:${currentSupportId ?? 'none'}`}
              dark={false}
              mode="single"
              items={mergedSupportOptions}
              onChange={(value: ListboxSelected) => {
                const supportId = value[0];
                const selectedOption = mergedSupportOptions.find(
                  option => String(option.value) === supportId
                );
                applySupport(supportId, selectedOption?.label);
              }}
              identifiers={{
                key: 'value',
                value: 'value',
                label: 'label',
              }}
              selected={currentSupportId ? [currentSupportId] : []}
              onSearch={setSearchText}
            />
            {searchText && !mergedSupportOptions.find(({ label }) => label === searchText) && (
              <button
                className="flex w-full items-center gap-2 border-t border-neutral-20 px-3 py-2 text-left text-sm text-text-dark-10 hover:bg-neutral-10"
                onClick={() => handleCreateSupport(searchText)}
              >
                <PlusIcon />
                {t('Añadir')} &ldquo;{searchText}&rdquo;
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
